import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { UserUpdatePasswordModel } from './../../../../models/authentication/user-update-password.model';
import { UserService } from './../../../../services/authentication/user.service';
import { CustomValidators } from './../../../../validators/custom-validators';

@Component({
  selector: 'app-update-password-user',
  templateUrl: './update-password-user.component.html',
  styleUrls: ['./update-password-user.component.scss'],
  standalone: false
})
export class UpdatePasswordUserComponent implements OnInit, OnDestroy {
  formUserUpdatePassword: FormGroup = new FormGroup({});
  subscription: Subscription;
  userContract: UserModel = new UserModel();

  constructor(
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private toastHandlerService: ToastHandlerService,
    private userService: UserService) {
  }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.userContract = inputs.userModel;
      this.validateForm();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async send() {
    Object.keys(this.formUserUpdatePassword.controls).forEach(key => {
      const control = this.formUserUpdatePassword.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formUserUpdatePassword.valid) {
      const modelUser = this.formUserUpdatePassword.value as UserUpdatePasswordModel;
      try {
        const response = await firstValueFrom(this.userService.updateUserPassword(modelUser));
        this.toastHandlerService.showToast('success', `La contraseña fue modificada exitosamente.`);
        this.userContract = response;
        this.ref.close();
      } catch (error) {
        console.error('Error al actualizar la contraseña.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      passwordHash: {
        required: 'La contraseña es obligatoria.',
        minlength: 'La contraseña debe tener mínimo 8 caracteres.',
        hasNumber: 'La contraseña debe incluir al menos un número.',
        hasCapitalCase: 'La contraseña debe incluir al menos una letra mayúscula.',
        hasSmallCase: 'La contraseña debe incluir al menos una letra minúscula.',
        hasSpecialCharacters: 'La contraseña debe incluir al menos un carácter especial.',
      },
      confirmPasswordHash: {
        required: 'La confirmación de contraseña es obligatoria.',
        NoPassswordMatch: 'Las contraseñas no coinciden.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formUserUpdatePassword.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formUserUpdatePassword = this.formBuilder.group(
      {
        userId: [this.userContract.userId, [Validators.required, Validators.min(1)]],
        passwordHash: [null,
          Validators.compose([
            Validators.required,
            // verificar número
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true
            }),
            // verificar letras mayúsculas
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            // verificar letra minúscula
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            // verificar carácter especial
            CustomValidators.patternValidator(
              /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
              {
                hasSpecialCharacters: true
              }
            ),
            Validators.minLength(8)
          ])
        ],
        confirmPasswordHash: [null, Validators.compose([Validators.required])]
      },
      {
        // verificar confirmar contraseña coinciden
        validator: CustomValidators.passwordMatchValidator
      }
    );
  }
}

